/** Reading the game show's state. Pure, so every page and every test agree on what it says. */

import { attribute, currentAttribute, has, type CanvasState, type StateEntity } from "../../lib/runtime";

const NS = "activity.panel_stage.";

export const BOX = `${NS}box`;
export const TAKEN = `${NS}taken`;
export const CHARGED = `${NS}charged`;
export const SLOWED = `${NS}slowed`;
export const BUZZED = `${NS}buzzed`;
export const CORRECT = `${NS}correct`;
export const WRONG = `${NS}wrong`;
export const OCCUPANT = `${NS}occupant`;
export const NAMEPLATE = `${NS}nameplate`;
export const POWER = `${NS}power`;
export const COOLING = `${NS}cooling`;
export const ROUND = `${NS}round`;
export const VOTING = `${NS}voting`;
export const ASKING = `${NS}asking`;
export const ANSWERING = `${NS}answering`;

export const ENTRANT = "activity.drop_in.entrant";

const VOTE_OPEN = "activity.vote_window.open";

/** Whether picks are being taken, read off the window itself rather than the stage's mirror. */
export function votingIsOpen(state: CanvasState | null): boolean {
  return (state?.entities ?? []).some((one) => has(one, VOTE_OPEN));
}

/** The role that reaches a buzzer. press_race declares it, which is what offers it to a customer. */
export const CONTESTANT = "contestant";

export type Phase = "idle" | "voting" | "asking" | "answering";

export const ICONS = ["buff", "debuff", "attack"] as const;
export type Icon = (typeof ICONS)[number];

export const ICON_LABEL: Record<Icon, string> = {
  buff: "Charge",
  debuff: "Slow",
  attack: "Steal",
};

export const ICON_BLURB: Record<Icon, string> = {
  buff: "presses a second sooner, and scores double",
  debuff: "presses a second later",
  attack: "takes a point from whoever you pick",
};

export interface Box {
  place: number;
  points: number;
  taken: boolean;
  charged: boolean;
  slowed: boolean;
  buzzed: boolean;
  correct: boolean;
  wrong: boolean;
  who: string;
  /** The name somebody typed for this box, or empty when nobody has. */
  name: string;
  /** What to call this box anywhere it is referred to: the typed name, else its position. */
  label: string;
}

export interface Power {
  id: string;
  icon: Icon;
  place: number;
  tally: number;
  targetPlace: number;
  cooling: boolean;
}

function held(state: CanvasState | null, tag: string): StateEntity[] {
  return (state?.entities ?? []).filter((one) => has(one, tag));
}

/** Who is standing in each box, keyed by place. */
export function occupantsOf(state: CanvasState | null): Map<number, string> {
  const found = new Map<number, string>();
  for (const one of held(state, OCCUPANT)) found.set(attribute(one, "place"), one.id);
  return found;
}

export function boxesOf(state: CanvasState | null): Box[] {
  const who = occupantsOf(state);
  const named = namesOf(state);
  return held(state, BOX)
    .map((one) => {
      const place = attribute(one, "place");
      const sitting = who.get(place) ?? "";
      const name = named.get(place) ?? "";
      return {
        place,
        points: currentAttribute(one, "points"),
        taken: has(one, TAKEN),
        charged: has(one, CHARGED),
        slowed: has(one, SLOWED),
        buzzed: has(one, BUZZED),
        correct: has(one, CORRECT),
        wrong: has(one, WRONG),
        who: sitting,
        name,
        label: name === "" ? `Box ${String(place)}` : name,
      };
    })
    .sort((a, b) => a.place - b.place);
}

/**
 * What each box is called, keyed by place.
 *
 * The name IS the entity id, because no attribute holds a string anywhere in the platform, so it
 * arrives slugged and is rebuilt here.
 */
export function namesOf(state: CanvasState | null): Map<number, string> {
  const found = new Map<number, string>();
  for (const one of held(state, NAMEPLATE)) found.set(attribute(one, "place"), unslug(one.id));
  return found;
}

export function unslug(id: string): string {
  return id
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function powersOf(state: CanvasState | null): Power[] {
  return held(state, POWER)
    .map((one) => ({
      id: one.id,
      icon: (ICONS[attribute(one, "slot") - 1] ?? "buff") as Icon,
      place: attribute(one, "place"),
      tally: currentAttribute(one, "tally"),
      targetPlace: attribute(one, "target_place"),
      cooling: has(one, COOLING),
    }))
    .sort((a, b) => a.place - b.place || ICONS.indexOf(a.icon) - ICONS.indexOf(b.icon));
}

export function phaseOf(state: CanvasState | null): Phase {
  const round = held(state, ROUND)[0];
  if (round === undefined) return "idle";
  if (has(round, VOTING)) return "voting";
  if (has(round, ASKING)) return "asking";
  if (has(round, ANSWERING)) return "answering";
  return "idle";
}

export function roundNumberOf(state: CanvasState | null): number {
  const round = held(state, ROUND)[0];
  return round === undefined ? 0 : currentAttribute(round, "number");
}

/** Who is on the roster, which is who has taken a box. */
export function entrantsOf(state: CanvasState | null): string[] {
  return held(state, ENTRANT).map((one) => one.id);
}

/** Every option on one box, in icon order, with each steal target on its own. */
export function powersOn(powers: Power[], place: number): Power[] {
  return powers
    .filter((one) => one.place === place)
    .sort(
      (a, b) => ICONS.indexOf(a.icon) - ICONS.indexOf(b.icon) || a.targetPlace - b.targetPlace,
    );
}

/** A person id, shortened enough to fit on a box without being mistaken for a name. */
export function shortId(id: string): string {
  return id.length <= 10 ? id : `${id.slice(0, 8)}...`;
}
